import GlobalTextInputField from "@components/inputs/GlobalTextInputField";
import { userManagementApis } from "@services/apis/UserManagement";
import {
  GetAllPersonalTransactionRequest,
  TransactionDetails,
} from "@services/models/UserManagement/get-transaction";
import { GetUserBalancesResponse } from "@services/models/UserManagement/get-wallets";
import { useEffect, useRef, useState } from "react";
import { Menu, MenuItem, Sidebar } from "react-pro-sidebar";
import { useNavigate } from "react-router-dom";
import ConstantFunctions from "../../utils/constants_functions";
import TransactionFilter from "./wallet/TransactionFilter";
import TransactionRowComponent from "./wallet/TransactionRow";
import TransactionRowComponentSkeleton from "./wallet/TransactionRowSkeleton";

const Wallet = ({
  userId,
  accountType,
}: {
  userId: number;
  accountType: string;
}) => {
  const constantFunction = new ConstantFunctions();
  const navigate = useNavigate();
  const [walletData, setWalletData] = useState<GetUserBalancesResponse | null>(
    null,
  );
  const [loading, setLoading] = useState<boolean>(false);

  const [transactions, setTransactions] = useState<TransactionDetails[]>([]);
  const [selectedMerchantId, setSelectedMerchantId] = useState<number | null>(
    null,
  );
  const [isFilterOpen, setIsFilterOpen] = useState<boolean>(false);
  const [incomingSearchTerm, setIncomingSearchTerm] = useState<string>("");
  const [ledger, setLedger] = useState<string>("");
  const [currentTransactionId, setCurrentTransactionId] = useState<
    number | null
  >(null);
  const [transactionLoading, setTransactionLoading] = useState<boolean>(false);

  const [page, setPage] = useState<number>(1);
  const rows = 20;
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [collapsed, setCollapsed] = useState(true);
  const loaderRef = useRef<HTMLDivElement | null>(null);

  const getWallet = async () => {
    if (!userId) return;

    setLoading(true);
    try {
      const response = await userManagementApis.getWallets(userId);
      setWalletData(response);
    } catch (error) {
      console.error("Error fetching wallet:", error);
      setWalletData(null);
    } finally {
      setLoading(false);
    }
  };

  const getTransactions = async ({
    merchantId,
    pageNumber = 1,
    ledger,
    searchTerm,
  }: {
    merchantId?: number;
    pageNumber?: number;
    ledger?: string;
    searchTerm?: string;
  }) => {
    if (!userId) return;

    setTransactionLoading(true);

    try {
      let searchTerm;
      if (incomingSearchTerm.trim() !== "") {
        searchTerm = incomingSearchTerm.trim();
      }
      const request: GetAllPersonalTransactionRequest = {
        userId: userId,
        rows: rows,
        page: pageNumber,
        merchantId: merchantId,
        // ledger,
        // searchTerm,
      };
      const response = await userManagementApis.getUserTransactions(request);

      const newTransactions = response.data || [];

      if (pageNumber === 1) {
        setTransactions(newTransactions);
      } else {
        setTransactions((prev) => [...prev, ...newTransactions]);
      }

      setHasMore(newTransactions.length === rows);
      setPage(pageNumber);
    } catch (error) {
      console.error("Error fetching transactions:", error);
    } finally {
      setTransactionLoading(false);
    }
  };

  useEffect(() => {
    getWallet();
  }, [userId]);

  useEffect(() => {
    // reset transactions when user changes
    setTransactions([]);
    setSelectedMerchantId(null);
    setPage(1);
    setHasMore(true);
  }, [userId]);

  useEffect(() => {
    if (walletData && selectedMerchantId === null) {
      handleMerchantClick(0); // select personal wallet
    }
  }, [walletData]);

  useEffect(() => {
    if (!loaderRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          hasMore &&
          !transactionLoading &&
          selectedMerchantId !== null
        ) {
          getTransactions({
            merchantId: selectedMerchantId,
            pageNumber: page + 1,
          });
        }
      },
      { threshold: 1 },
    );

    observer.observe(loaderRef.current);

    return () => {
      if (loaderRef.current) observer.unobserve(loaderRef.current);
    };
  }, [page, hasMore, transactionLoading, selectedMerchantId]);

  const personalBalances = walletData?.data?.personalBalances || [];
  const merchantBalances = walletData?.data?.merchantBalances || [];

  const getSelectedCurrencyImage = (symbol: string) => {
    return (
      personalBalances.find((b) => b.currencySymbol === symbol)
        ?.currencyImage || ""
    );
  };

  function sendToTransactionDetailScreen(
    transactionID: number,
    ledger: string,
  ) {
    localStorage.setItem("comingFromTransactionDetails", "true");
    navigate(`/${transactionID}/${ledger}`, {
      state: {
        userId: userId,
        merchantId: selectedMerchantId,
        path: "",
      },
    });
  }

  const getSelectedBalance = (symbol: string) => {
    // Personal wallet
    if (selectedMerchantId === 0) {
      const balance = personalBalances.find(
        (b) => b.currencySymbol === symbol,
      )?.balance;

      return balance ? constantFunction.formatAmount(balance) : "-";
    }

    // Merchant wallet
    const merchant = merchantBalances.find(
      (m) => m.merchantId === selectedMerchantId,
    );

    const balance = merchant?.balances?.find(
      (b: any) => b.currencySymbol === symbol,
    )?.balance;

    return balance
      ? constantFunction.formatAmount(balance)
      : constantFunction.formatAmount("0");
  };

  const handleMerchantClick = (merchantId?: number) => {
    setSelectedMerchantId(merchantId ?? 0);
    setPage(1);
    setHasMore(true);
    getTransactions({ merchantId, pageNumber: 1 });

    if (collapsed) {
      setCollapsed(false);
    }
  };

  const handleIncomingSearchChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = e.target.value.trim();
    setIncomingSearchTerm(value.trim());
  };

  return (
    <div className="flex w-full">
      {/* Wallet Sidebar */}
      <Sidebar
        collapsed={collapsed}
        collapsedWidth="60px"
        width="180px"
        backgroundColor={"#F8F8F8"}
        onMouseEnter={() => setCollapsed(false)}
        onMouseLeave={() => setCollapsed(true)}>
        <Menu
          menuItemStyles={{
            button: {
              padding: "6px 10px", // reduces vertical + horizontal padding
            },
            icon: {
              marginRight: "6px", // reduces gap between icon and text
              minWidth: "22px", // prevents large icon container
            },
            label: {
              fontSize: "12px", // wallet name font size
              fontWeight: 500,
            },
          }}>
          {/* Personal wallet */}
          <MenuItem
            icon={
              <span className="material-symbols-outlined text-[18px] mr-0 pr-0">
                account_balance_wallet
              </span>
            }
            className={
              selectedMerchantId === 0
                ? "text-text-navbar-active"
                : "text-text-base"
            }
            onClick={() => handleMerchantClick()}>
            {!collapsed && (
              <span className="text-[12px] font-medium ml-0 pl-0">
                Personal Wallet
              </span>
            )}
          </MenuItem>

          {/* Merchant wallets */}
          {merchantBalances.map((merchant: any) => (
            <MenuItem
              key={merchant.merchantId}
              icon={
                <span className="material-symbols-outlined text-[18px]">
                  store
                </span>
              }
              className={
                selectedMerchantId === merchant.merchantId
                  ? "text-text-navbar-active"
                  : "text-text-base"
              }
              onClick={() => handleMerchantClick(merchant.merchantId)}>
              {!collapsed && (
                <span className="text-[12px] font-medium">
                  {merchant.accountCategoryName}
                </span>
              )}
            </MenuItem>
          ))}
        </Menu>
      </Sidebar>
      {/* Transactions Area */}
      <div className="flex-1 min-w-0">
        {selectedMerchantId !== null && (
          <div className=" bg-bg-base flex flex-col">
            <span className="text-[12px] font-medium p-[16px]">BALANCE</span>
            <div className="flex flex-row justify-between mb-[8px] px-[16px]">
              <span className="text-[10px] font-medium w-[16.6%] text-left">
                USDT
              </span>
              <span className="text-[10px] font-medium w-[16.6%] text-center">
                USDC
              </span>
              <span className="text-[10px] font-medium w-[16.6%] text-right">
                DAI
              </span>
            </div>
            <div
              className="flex flex-row justify-between px-[16px] pb-[8px] mb-[8px] cursor-pointer border-b border-b-border-subtle"
              onClick={() => handleMerchantClick()}>
              {["USDT", "USDC", "DAI"].map((symbol, index) => (
                <div
                  key={symbol}
                  className={`flex flex-row w-[16.6%] items-center ${
                    index === 0
                      ? "justify-start"
                      : index === 1
                        ? "justify-center"
                        : "justify-end"
                  }`}>
                  <img
                    src={getSelectedCurrencyImage(symbol)}
                    alt={symbol}
                    className="h-[12px] w-[12px] mr-[4px]"
                  />
                  <span className="font-semibold text-[12px]">
                    {getSelectedBalance(symbol)}
                  </span>
                </div>
              ))}
            </div>
            <div className="flex flex-col gap-[8px] border-b border-b-border-subtle px-[16px] py-[8px] mb-[8px]">
              <div className="flex flex-row justify-between items-center">
                <span className="text-[12px] font-medium">FILTER</span>
                <div
                  className="rounded-full px-[8px] py-[2px] border border-border-subtle text-[10px] cursor-pointer font-medium"
                  onClick={() => setIsFilterOpen(true)}>
                  FILTERS
                </div>
              </div>
              <GlobalTextInputField
                type="text"
                value={incomingSearchTerm}
                onChange={handleIncomingSearchChange}
                placeholder="Search by Merchant Order ID / Transaction Hash / Address"
                prefixIcon={
                  <span className="material-symbols-outlined text-text-subtle">
                    search
                  </span>
                }
              />
            </div>
            <span className="text-[12px] font-semibold px-[16px]">
              Transactions
            </span>
            <div className="mt-[8px] flex flex-col px-[16px]">
              {transactions.length === 0 && !transactionLoading ? (
                <span className="text-[10px] text-text-subtle">
                  No transactions found.
                </span>
              ) : (
                transactions.map((transaction) => (
                  <TransactionRowComponent
                    transaction={transaction}
                    isSelected={
                      currentTransactionId === transaction.transactionId
                    }
                    onRowClick={() => {
                      setCurrentTransactionId(transaction.transactionId);
                      sendToTransactionDetailScreen(
                        transaction.transactionId,
                        transaction.ledger,
                      );
                    }}
                  />
                ))
              )}

              {transactionLoading &&
                Array.from({ length: 3 }).map((_, index) => (
                  <TransactionRowComponentSkeleton key={index} />
                ))}

              <div ref={loaderRef} />
            </div>
          </div>
        )}
      </div>
      <TransactionFilter
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        onApply={(filters) => {
          setLedger(filters.ledger || "");

          getTransactions({
            merchantId: selectedMerchantId || 0,
            pageNumber: 1,
            ledger: filters.ledger,
            searchTerm: incomingSearchTerm,
          });
        }}
      />
    </div>
  );
};

export default Wallet;
